import { defineMessages, type IntlShape, useIntl } from 'react-intl';
import classNames from 'classnames';

import { Wysiwyg as SVG } from '@jsmdg/yoshi';
import { AttributeType } from '../../../shared/enums';
import { type ProductAttribute } from '../../../shared/types';
import { Divider } from '../Divider/Divider';
import styles from './AttributesContainer.module.scss';

type AttributesContainerProps = {
    readonly attributes?: ProductAttribute[];
};

const messages = defineMessages({
    durationLabel: {
        defaultMessage:
            '{days, plural, =0 {} one {{days} Tag} other {{days} Tage}} {hours, plural, =0 {} one {{hours} Stunde} other {{hours} Stunden}} {minutes, plural, =0 {} one {{minutes} Minute} other {{minutes} Minuten}}',
    },
});

function getAttributeDisplayValue(attribute: ProductAttribute, intl: IntlShape): string {
    if (attribute.type === AttributeType.QUANTITY) {
        return `${attribute.value} ${attribute.unit} ${attribute.title}`;
    }

    if (attribute.type === AttributeType.NUMERIC) {
        return `${attribute.value} ${attribute.title}`;
    }

    if (attribute.type === AttributeType.DURATION) {
        return intl.formatMessage(messages.durationLabel, {
            days: attribute.value.days ?? 0,
            hours: attribute.value.hours ?? 0,
            minutes: attribute.value.minutes ?? 0,
        });
    }

    if (attribute.type === AttributeType.STRING || attribute.type === AttributeType.HTML) {
        return attribute.value;
    }

    return attribute.title;
}

function isProductAttributes(
    attributes: ProductAttribute[] | undefined,
): attributes is ProductAttribute[] {
    return Boolean(attributes?.length);
}

const AttributesContainer = ({ attributes }: AttributesContainerProps): JSX.Element => {
    const intl = useIntl();

    return (
        <>
            {isProductAttributes(attributes) && (
                <div className={classNames(styles.container, 'pb-sm-3x')}>
                    <Divider className="d-block d-sm-none" />
                    <div className="pt-2x pt-sm-0 pb-3x pb-sm-0 d-flex flex-wrap">
                        {attributes.map(attribute => {
                            const value = getAttributeDisplayValue(attribute, intl);
                            return (
                                <div className="d-flex mr-4x pt-1x" key={value}>
                                    {/* eslint-disable-next-line react/jsx-pascal-case */}
                                    <SVG
                                        content={attribute.icon}
                                        key={attribute.title}
                                        className={classNames('mr-1x w-auto', styles.themedIcon)}
                                    />
                                    <span>{value}</span>
                                </div>
                            );
                        })}
                    </div>
                    <Divider className="d-block d-sm-none" />
                </div>
            )}
        </>
    );
};

export { AttributesContainer };
